import { useState } from "react";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

import { Calendar } from "../../../components/shadcn/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/shadcn/popover";
import Text from "../../../components/ui/Text";

const WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"];

export default function DateFieldJP({
  date,
  onChange,
  disabled,
  fromDate,
  toDate,
  isLoading,
}: {
  date: Date | undefined;
  onChange: (date: Date | undefined) => void;
  disabled?: (date: Date) => boolean;
  fromDate?: Date;
  toDate?: Date;
  isLoading?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-12 w-full cursor-pointer items-center rounded-lg border-2 border-solid border-grey2 bg-white px-3"
        >
          {date ? (
            <Text className="flex-1 text-left font-medium">
              {format(date, "yyyy/MM/dd")}
            </Text>
          ) : (
            <Text className="flex-1 text-left text-grey4">yyyy/mm/dd</Text>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        sideOffset={12}
        className="w-[var(--radix-popover-trigger-width)] overflow-hidden  p-0 shadow-custom0"
      >
        {isLoading ? (
          <div className="flex h-[280px] items-center justify-center">
            <Loader2 className="size-6 animate-spin text-orange2" />
          </div>
        ) : (
          <Calendar
            mode="single"
            selected={date}
            onSelect={(selected) => {
              if (selected) onChange(selected);
              setOpen(false);
            }}
            disabled={disabled}
            defaultMonth={fromDate}
            fromDate={fromDate}
            toDate={toDate}
            showOutsideDays={false}
            formatters={{
              formatCaption: (month) => format(month, "yyyy年M月"),
              formatWeekdayName: (weekday) => WEEKDAYS[weekday.getDay()],
            }}
            className="w-full p-0"
            components={{
              IconLeft: () => <ChevronLeft className="size-6 text-black8" />,
              IconRight: () => <ChevronRight className="size-6 text-black8" />,
            }}
            classNames={{
              caption: "flex h-9 items-center justify-center gap-1.5",
              caption_label: "order-2 text-[15px] font-semibold text-black8",
              nav: "contents",
              nav_button:
                "inline-flex size-6 items-center justify-center bg-transparent p-0 opacity-100 hover:opacity-100 disabled:opacity-40",
              nav_button_previous: "order-1",
              nav_button_next: "order-3",
              head_cell: "flex-1 text-[13px] font-medium text-black6",
              cell: "flex-1 p-0 text-center [&:has([aria-selected])]:bg-transparent",
              day: "inline-flex size-10 items-center justify-center rounded-full p-0 text-[15px] text-black8 hover:bg-transparent",
              day_selected:
                "bg-orange2 text-white hover:bg-orange2 hover:text-white focus:bg-orange2 focus:text-white",
              day_today: "bg-transparent",
              day_disabled: "text-grey4 opacity-100",
            }}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
